import type { Project } from '@shared/models/project.model';
import { getBuildingVersionById } from '@shared/helpers/recursive-element.helpers';
import type { ProposalId } from '@shared/models/proposals.model';

/**
 * First proposal to select after a version has been added. Version creation guarantees at
 * least one proposal, but this stays defensive so failed/legacy snapshots clear the query
 * param instead of carrying a proposal from the previous version.
 */
export const getFirstProposalIdForVersion = (
  project: Project,
  versionId: string,
): ProposalId | undefined =>
  getBuildingVersionById(project, versionId, false)?.proposals?.[0]?.id;

/**
 * Whether deleting {@link removedVersionId} must also move the route.
 *
 * The URL is authoritative while UI-store selection catches up during version
 * animations. Checking both prevents a deleted route from being left behind
 * when the store still points at the previous/active version.
 */
export const shouldNavigateAfterVersionRemove = (
  removedVersionId: string,
  routeVersionId: string | undefined,
  selectedVersionId: string | undefined,
): boolean =>
  removedVersionId === routeVersionId || removedVersionId === selectedVersionId;

/**
 * Version to select after deleting one from the version switcher.
 * {@link orderedVersionIds} must be the same left-to-right order as the rail.
 * Always the item immediately before the deleted one, except when the first
 * item is deleted — then the new first remaining item.
 */
export const getVersionIdToSelectAfterRemove = (
  orderedVersionIds: readonly string[],
  removedVersionId: string,
): string | undefined => {
  const removedIndex = orderedVersionIds.indexOf(removedVersionId);
  if (removedIndex < 0) return undefined;
  if (removedIndex === 0) return orderedVersionIds[1];
  return orderedVersionIds[removedIndex - 1];
};

/**
 * Route update to run before/with a version delete when that version is the
 * one currently shown. Destination follows the switcher's left-to-right ids.
 * `flushSync` is required so the URL leaves the deleted version before the
 * optimistic store update. Undefined when another version is selected or
 * nothing remains to open.
 */
export const getVersionDeleteNavigation = (
  orderedVersionIds: readonly string[],
  removedVersionId: string,
  selection: {
    routeVersionId: string | undefined;
    selectedVersionId: string | undefined;
  },
): { versionId: string; flushSync: true } | undefined => {
  if (
    !shouldNavigateAfterVersionRemove(
      removedVersionId,
      selection.routeVersionId,
      selection.selectedVersionId,
    )
  ) {
    return undefined;
  }
  const versionId = getVersionIdToSelectAfterRemove(
    orderedVersionIds,
    removedVersionId,
  );
  if (!versionId) return undefined;
  return { versionId, flushSync: true };
};

/**
 * Version to open after an optimistic add if the minted version is gone from
 * {@link project} (persist/enrich revert). Pass the live store project, not an
 * error-handler return value — those can disagree. Undefined when the add is
 * still present or there is no previous version to return to.
 */
export const getVersionIdToRestoreAfterFailedAdd = (
  mintedVersionId: string,
  previousVersionId: string | undefined,
  project: Project,
): string | undefined => {
  if (getBuildingVersionById(project, mintedVersionId, false)) return undefined;
  if (!previousVersionId) return undefined;
  if (!getBuildingVersionById(project, previousVersionId, false)) {
    return undefined;
  }
  return previousVersionId;
};

/**
 * Version to open after an optimistic delete if {@link removedVersionId} is back
 * on {@link project} (persist/enrich revert). Pass the live store project, not
 * an error-handler return value. Undefined when the delete stuck.
 */
export const getVersionIdToRestoreAfterFailedRemove = (
  removedVersionId: string,
  project: Project,
): string | undefined => {
  if (!getBuildingVersionById(project, removedVersionId, false)) {
    return undefined;
  }
  return removedVersionId;
};
